import { datadogRum } from '@datadog/browser-rum';
import { registerRumOperationTracker } from '@circuitry-ai/doc-data';
import { initPanelContext } from './rumIdentity';
import { setRumGlobalContext } from './rumContext';
import {
  markDatadogRumReady,
  runWithRumTracking,
  setRumTelemetryConsent,
} from './rumOperationTracker';

export type RumTenantSettingRow = {
  ability?: string;
  is_active?: boolean;
  ability_value?: number;
  value?: number;
  default_value?: number;
};

let rumInitialized = false;

function capturePercentage(setting: RumTenantSettingRow | undefined): number {
  const raw = Number(setting?.ability_value ?? setting?.value ?? setting?.default_value ?? 0);
  if (!Number.isFinite(raw)) return 0;
  return Math.min(100, Math.max(0, Math.round(raw)));
}

function datadogAlreadyInitialized(): boolean {
  try {
    return (
      typeof datadogRum.getInitConfiguration === 'function' &&
      datadogRum.getInitConfiguration() != null
    );
  } catch {
    return false;
  }
}

function bindRumOperationTracking(): void {
  markDatadogRumReady();
  registerRumOperationTracker(runWithRumTracking);
}

function initDatadog(sessionSampleRate: number): void {
  const appVersion = import.meta.env.CTY_APP_VERSION ?? 'unknown';
  datadogRum.init({
    applicationId: import.meta.env.CTY_DATA_DOG_APP_ID,
    clientToken: import.meta.env.CTY_DATA_DOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.CTY_DATA_DOG_SERVICE_NAME,
    env: import.meta.env.CTY_MODE,
    version: appVersion,
    sessionSampleRate,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: (() => {
      const pageOrigin = (globalThis as Window & typeof globalThis).location.origin;
      const backendOrigins = new Set(
        [
          import.meta.env.CTY_API_URL,
          import.meta.env.CTY_AGENTS_STREAM_URL,
          import.meta.env.CTY_AGENTIC_ANALYST_URL,
          import.meta.env.CTY_VOICE_AGENT_BASE_URL,
        ]
          .filter(Boolean)
          .map((u) => {
            try {
              return new URL(u, pageOrigin).origin;
            } catch {
              return null;
            }
          })
          .filter(Boolean),
      );
      backendOrigins.add(pageOrigin);

      const rawAuthDomain = String(import.meta.env.CTY_APP_DOMAIN ?? '').trim();
      const hasProtocol =
        rawAuthDomain.toLowerCase().startsWith('http://') ||
        rawAuthDomain.toLowerCase().startsWith('https://');
      const authDomain = rawAuthDomain
        ? (() => {
            try {
              return new URL(hasProtocol ? rawAuthDomain : `https://${rawAuthDomain}`).hostname.toLowerCase();
            } catch {
              return rawAuthDomain.toLowerCase();
            }
          })()
        : '';
      const isCircuitryAiSubdomain = (hostname: string) =>
        hostname.toLowerCase().endsWith('.circuitry.ai');

      return [
        {
          match: (url: string) => {
            try {
              const { hostname, origin } = new URL(url, pageOrigin);
              if (authDomain && hostname.toLowerCase() === authDomain) return false;
              return backendOrigins.has(origin) || isCircuitryAiSubdomain(hostname);
            } catch {
              return false;
            }
          },
          propagatorTypes: ['tracecontext'],
        },
      ];
    })(),
    traceSampleRate: 100,
  });
  bindRumOperationTracking();
  initPanelContext();
  setRumGlobalContext({ surface: 'web' });
  rumInitialized = true;
}

/** Start or stop Datadog from the tenant RUM setting. Sample rate is locked at init. */
export function applyRumSettings(settings: RumTenantSettingRow[] | undefined): void {
  if (import.meta.env.PROD !== true || !settings) return;

  const rum = settings.find((entry) => String(entry.ability ?? '').toLowerCase() === 'rum');
  const enabled = Boolean(rum?.is_active);
  const percentage = capturePercentage(rum);

  if (!enabled || percentage <= 0) {
    setRumTelemetryConsent(false);
    if (rumInitialized || datadogAlreadyInitialized()) {
      datadogRum.setTrackingConsent('not-granted');
    }
    return;
  }

  if (!rumInitialized) {
    if (datadogAlreadyInitialized()) {
      bindRumOperationTracking();
      setRumGlobalContext({ surface: 'web' });
      rumInitialized = true;
    } else {
      initDatadog(percentage);
    }
    setRumTelemetryConsent(true);
    datadogRum.setTrackingConsent('granted');
    return;
  }

  setRumTelemetryConsent(true);
  datadogRum.setTrackingConsent('granted');
}
